import { CustomLoadingOverlay } from "@/controls/customLoader/CustomCircularProgress";
import { ATHENS_GRAY } from "@/controls/shared/constants/colors";

import { Grid2 as Grid, Paper, ThemeProvider, Typography } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "@mui/styles";
import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChemistryHeatReport } from "../../caster/repositories/ProductionRepository";
import { useHeatReportContext } from "../../meltshop/heat-report/HeatReportContext";
import { EditCastedGrade } from "./components/modals/EditCastedGrade";
import "./heatReport.css";
import heatReportStyles from "@/pages/heat-report/styles/useHeatReportStyles";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { Theme } from "@mui/material/styles";
import { searchHeatsWithProcessId } from "../../meltshop/heats/repositories/heatsRepository";
import { ChemistryHeatReportHeaderFields } from "./components/HeaderFields";
import { ElementTable } from "./components/element-table/ElementTable";

type SearchedHeat = {
	Id: number;
	Name: string;
};

type Props = {};

const useStyles = makeStyles((theme: Theme) => ({
	infoCardContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		[theme.breakpoints.down("sm")]: {
			justifyContent: "flex-start",
		},
	},
	infoCard: {
		maxWidth: "200px",
		minWidth: "170px",
		width: "100%",
	},
}));

export const ChemistryHeatReportPage = (props: Props) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const { state, actions } = useHeatReportContext();

	const { heatName: heatNameParam } = useParams();
	const [heatName, setHeatName] = useState(heatNameParam || null);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [showGradeModal, setShowGradeModal] = React.useState(false);
	const [dataSource, setDataSource] = React.useState<any[]>([]);
	const [coloringEnabled, setColoringEnabled] = React.useState(true);

	useEffect(() => {
		setHeatName(heatNameParam ?? null);
	}, [heatNameParam]);

	const getStringValue = (dataValue: any | undefined) => {
		if (dataValue === undefined || dataValue === null) return "";
		return String(dataValue);
	};

	const getHeatByName = async (heatNameToSearch: string) => {
		const response = await searchHeatsWithProcessId(heatNameToSearch);
		if (
			response.ok &&
			response.data &&
			response.data.tables[0]?.rows.length > 0
		) {
			const heats: SearchedHeat[] = response.data.tables[0].rows;
			const foundRawHeat = heats.find((h) => h.Name === heatNameToSearch);

			const heatToSelect = foundRawHeat || heats[0];

			if (heatToSelect) {
				const newHeat = {
					HeatId: heatToSelect.Id,
					HeatName: heatToSelect.Name,
					IsExternal: false, // Assuming default
					NextHeatId: null,
					PreviousHeatId: null,
					NextHeatName: null,
					PreviousHeatName: null,
					ProcessId: null,
				};
				actions.setSelectedHeat(newHeat);
			}
		}
	};

	const getChemistryData = async (heatId: number | null) => {
		setIsLoading(true);

		const resp = await getChemistryHeatReport(heatId);
		if (resp.ok && resp.data && resp.data.tables.length > 1) {
			const heatInfo = resp.data.tables[0].rows[0];
			const samples = resp.data.tables[1].rows;

			actions.setChemistryHeatReport(heatInfo);
			setDataSource(samples || []);
		} else {
			actions.setChemistryHeatReport(null);
			setDataSource([]);
			if (!resp.ok) {
				console.error(resp.message);
			}
		}

		setIsLoading(false);
	};

	const handleNext = () => {
		const heat = state.chemistryHeatReport;
		if (heat?.NextHeatId && heat?.NextHeatName) {
			const newHeat = {
				HeatId: heat.NextHeatId,
				HeatName: heat.NextHeatName,
				IsExternal: heat.IsExternal || false,
				NextHeatId: null,
				PreviousHeatId: null,
				NextHeatName: null,
				PreviousHeatName: null,
				ProcessId: null,
			};

			actions.setSelectedHeat(newHeat);

			navigate(`/heat-report/chemistry/${heat.NextHeatName}`);
		}
	};

	const handlePrevious = () => {
		const heat = state.chemistryHeatReport;
		if (heat?.PreviousHeatId && heat?.PreviousHeatName) {
			const newHeat = {
				HeatId: heat.PreviousHeatId,
				HeatName: heat.PreviousHeatName,
				IsExternal: heat.IsExternal || false,
				NextHeatId: null,
				PreviousHeatId: null,
				NextHeatName: null,
				PreviousHeatName: null,
				ProcessId: null,
			};

			actions.setSelectedHeat(newHeat);

			navigate(`/heat-report/chemistry/${heat.PreviousHeatName}`);
		}
	};

	useEffect(() => {
		const heatId = state.selectedHeat?.HeatId ?? null;
		getChemistryData(heatId);
	}, [state.selectedHeat?.HeatId]);

	useEffect(() => {
		if (heatNameParam && heatNameParam !== state.selectedHeat?.HeatName) {
			getHeatByName(heatNameParam);
		} else if (!heatNameParam && !state.selectedHeat?.HeatId) {
			getChemistryData(null);
		}
	}, [heatNameParam, state.selectedHeat?.HeatName]);

	return (
		<>
			<HelmetDexteel
				title={`${state.chemistryHeatReport?.HeatName ? state.chemistryHeatReport?.HeatName + " - " : ""} Chemistry Heat Report`}
			/>
			<Backdrop open={state.isLoadingBackground} style={{ zIndex: 1000 }}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Paper
				elevation={0}
				style={{
					width: "100%",
					padding: "15px 20px 10px",
					height: "93vh",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				<Grid container justifyContent="center" alignItems="center">
					<Grid size={{ xs: 12, md: 12 }} style={{ margin: 0 }}>
						<Grid size={{ xs: 12, md: 12 }}>
							<Typography variant="h5" style={{ fontWeight: 600 }}>
								Chemistry Heat Report
							</Typography>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<Grid
								container
								direction="row"
								justifyContent="flex-start"
								alignItems="center"
							></Grid>
						</Grid>
						{state.chemistryHeatReport !== undefined &&
							state.chemistryHeatReport !== null && (
								<ThemeProvider theme={heatReportStyles}>
									<ChemistryHeatReportHeaderFields
										getStringValue={getStringValue}
										setShowGradeModal={setShowGradeModal}
										getDataFromAPI={getChemistryData}
										onNext={handleNext}
										onPrevious={handlePrevious}
									/>
								</ThemeProvider>
							)}
						{state.chemistryHeatReport !== undefined &&
							state.chemistryHeatReport !== null && (
								<>
									<Grid container>
										<Grid size={{ xs: 12 }}>
											{isLoading ? (
												<div style={{ padding: "20px" }}>
													<CustomLoadingOverlay />
												</div>
											) : (
												<ElementTable
													elementList={dataSource}
													coloringEnabled={coloringEnabled}
													onToggleColoring={setColoringEnabled}
												/>
											)}
										</Grid>
									</Grid>
								</>
							)}
					</Grid>
				</Grid>
			</Paper>
			<EditCastedGrade
				show={showGradeModal}
				onClose={(shouldUpdate) => {
					setShowGradeModal(false);
					if (shouldUpdate) {
						getChemistryData(state.selectedHeat?.HeatId ?? null);
					}
				}}
				gradeId={state.chemistryHeatReport?.CastedGradeId || null}
				heatId={state.selectedHeat?.HeatId || null}
			/>
		</>
	);
};
